import React from 'react'
import {
  Button,
  Cascader,
  Form,
  Input,
  MenuProps,
  Modal,
  Select,
  Tooltip,
  Typography,
  message
} from 'antd'
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined,
  SaveOutlined
} from '@ant-design/icons'
import { Link, useNavigate, useParams } from 'react-router-dom'
import classNames from 'classnames'
import { AxiosError } from 'axios'
import { config, MdEditor } from 'md-editor-rt'
import RU from '@vavt/cm-extension/dist/locale/ru'
import { InfoBlock, LargeText, OrangeButton } from 'components/ui'
import { productApi } from 'api/product'
import {
  EActionsProductRequest,
  EProductStatus,
  EProductType,
  EUnit,
  ICharacteristic,
  ICreateProduct,
  IFrontVariation,
  IParentAndVariationsResponse,
  IParsedCategory,
  IPreviewData,
  IProductFormData,
  IUpdateProduct
} from 'models/product'
import { ERoles } from 'models/auth'
import { sellerApi } from 'api/seller'
import {
  ESellerDisableReason,
  ESellerStatus,
  ISeller,
  IShopDetails
} from 'models/seller'
import { appSelectors } from 'store/app'
import { AnchorsWrapper } from 'components/layout/AnchorsWrapper/AnchorsWrapper'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { showServerError } from 'shared/utils/showServerError'
import {
  floatNumbersPattern,
  nonZeroPattern,
  numbersPattern
} from 'shared/utils/patterns'
import { showMessage } from 'shared/utils/showMessage'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { IMAGES_URL, STOREFRONT_URL } from 'shared/constants/urls'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { TVariantMode } from 'models/app'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import {
  ENABLE_PRODUCT_STATUS_KEY,
  getStatusAfterAction,
  MIN_PRODUCT_IMAGE_SIDE,
  TAX_OPTIONS
} from 'shared/constants/product'
import { ProductPreview } from '../ProductPreview/ProductPreview'
import { ProductCardPreview } from '../ProductCardPreview/ProductCardPreview'
import { AiEnhancementModal } from '../ai/AiEnhancementModal/AiEnhancementModal'
import { PriceHistoryModal } from '../PriceHistory/PriceHistoryModal'
import { IAiEnhancementApplyData } from '../ai/AiEnhancementModal/types'
import styles from './ProductForm.module.scss'
import { getPreviewData } from './utils/getPreviewData'
import { getCreateSubmitData, getEditSubmitData } from './utils/getSubmitData'
import { validateProductForm } from './utils/formValidation'
import { getSiteUnavailableReason } from './utils/getSiteUnavailableReason'
import { CreateCategory } from './ui/CreateCategory'
import { ProductStatus } from './ui/ProductStatus'
import { ProductInfoBanner } from './ui/ProductInfoBanner'
import { ProductRating } from './ui/ProductRating'
import { Characteristics } from './ui/Characteristics'
import { StockFields } from './ui/StockFields'
import { DeliveryRestrictionNote } from './ui/DeliveryRestrictionNote'
import { validateVariations } from './utils/variations'
import {
  deriveVariationAxes,
  getCharacteristicValue
} from './utils/variationAxes'
import { toVariationProduct } from './utils/variationUtils'
import 'md-editor-rt/lib/style.css'

interface IProductFormProps {
  mode: TVariantMode
}

const anchors: MenuProps['items'] = [
  { label: 'Информация о товаре', key: '#info' },
  { label: 'Габариты', key: '#dimensions' },
  { label: 'Наполнение', key: '#filling' }
]

config({
  editorConfig: {
    languageUserDefined: {
      ru: RU
    }
  }
})

export const ProductForm: React.FC<IProductFormProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const sellerId = useAppSelector(appSelectors.selectSellerId)
  const role = useAppSelector(appSelectors.selectRole)
  const isAdmin = role === ERoles.ADMIN

  const isModeCreate = mode === 'create'

  const [form] = Form.useForm()

  const [preview, setPreview] = React.useState({
    open: false,
    data: null as IPreviewData | null
  })

  const [sellerInfo, setSellerInfo] = React.useState<ISeller | null>(null)
  const [isAiEnhancementModalOpen, setIsAiEnhancementModalOpen] =
    React.useState(false)
  const [aiButtonCoordinates, setAiButtonCoordinates] =
    React.useState<DOMRect | null>(null)
  const aiButtonRef = React.useRef<HTMLButtonElement>(null)

  const [categories, setCategories] = React.useState<IParsedCategory[]>([])
  const [selectedCategoryName, setSelectedCategoryName] =
    React.useState<string>('')
  const [selectedSubCategoryName, setSelectedSubCategoryName] =
    React.useState<string>('')

  const [isLoading, setIsLoading] = React.useState(isModeCreate ? false : true)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isError, setIsError] = React.useState(false)
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = React.useState(false)
  const [isPriceHistoryOpen, setIsPriceHistoryOpen] = React.useState(false)

  const [productName, setProductName] = React.useState<string>('')

  const [deletedPhotos, setDeletedPhotos] = React.useState<string[]>([])
  const [deletedVariations, setDeletedVariations] = React.useState<string[]>([])

  const [acceptedAiGeneratedImage, setAcceptedAiGeneratedImage] =
    React.useState<string | undefined>()
  const [parentAndVariationsData, setParentAndVariationsData] =
    React.useState<IParentAndVariationsResponse | null>(null)
  const [isDetaching, setIsDetaching] = React.useState(false)
  const [ratingScore, setRatingScore] = React.useState<number | undefined>(
    undefined
  )
  // Сохранённая версия товара: «Открыть» ведёт на неё, а не на правки в форме
  const [savedProduct, setSavedProduct] = React.useState<{
    slug?: string
    photo?: string[]
    f_price?: number | null
  } | null>(null)
  const [cardPreview, setCardPreview] = React.useState<IPreviewData | null>(
    null
  )

  const productId: string | undefined = Form.useWatch('id', {
    form,
    preserve: true
  })
  const number: string | undefined = Form.useWatch('number', {
    form,
    preserve: true
  })
  const article: string | undefined = Form.useWatch('article', {
    form,
    preserve: true
  })
  const productStatus: EProductStatus | undefined = Form.useWatch('status', {
    form,
    preserve: true
  })
  const productType: EProductType | undefined = Form.useWatch('type', {
    form,
    preserve: true
  })
  const seller:
    | {
        _id: string
        shop_details: IShopDetails
        is_disabled?: boolean
        disabled_reasons?: ESellerDisableReason[]
        status?: ESellerStatus
      }
    | undefined = Form.useWatch('seller', { form, preserve: true })
  const currentDescription: string =
    Form.useWatch('desc', { form, preserve: true }) ?? ''
  const currentPhotos =
    Form.useWatch('photo', { form, preserve: true }) ?? []

  const variations: IFrontVariation[] =
    Form.useWatch('variations', {
      form,
      preserve: true
    }) ?? []
  const categoriesId: string[] | undefined = Form.useWatch('categories_id', {
    form,
    preserve: true
  })

  const deliveryRestriction = React.useMemo(() => {
    const [categoryId, subCategoryId] = categoriesId ?? []
    return categories
      .find((category) => category.value === categoryId)
      ?.children?.find((subCategory) => subCategory.value === subCategoryId)
      ?.delivery_restriction
  }, [categories, categoriesId])

  const firstVariationName = React.useMemo(() => {
    return variations.find((el) => el.firstCharacteristicName)
      ?.firstCharacteristicName
  }, [variations])

  const secondVariationName = React.useMemo(() => {
    return variations.find((el) => el.secondCharacteristicName)
      ?.secondCharacteristicName
  }, [variations])

  const getCategories = async () => {
    try {
      const response = await productApi.getCategories({
        parsed: 'true'
      })
      // Бэкенд отдаёт подкатегории без сортировки — упорядочиваем по алфавиту для Cascader
      const sortedCategories = (response.data as IParsedCategory[]).map(
        (category) => ({
          ...category,
          children: [...(category.children ?? [])].sort((a, b) =>
            a.title.localeCompare(b.title, 'ru')
          )
        })
      )
      setCategories(sortedCategories)
    } catch (e) {
      showServerError(e)
    }
  }

  const onDeleteProduct = async () => {
    try {
      await productApi.setActions({ toDelete: productId })
      message.success('Товар успешно удален')
      navigate(-1)
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const changeStatus = async (statusKey: string) => {
    if (!productId) {
      return
    }
    try {
      if (statusKey === EProductStatus.DISABLED) {
        message.loading('Отключение товара')
        const response = await productApi.toggleProductDisabled(productId, true)
        form.setFieldValue('status', response.data.status)
        message.destroy()
        message.success('Товар отключен')
        return
      }

      if (statusKey === ENABLE_PRODUCT_STATUS_KEY) {
        message.loading('Включение товара')
        const response = await productApi.toggleProductDisabled(
          productId,
          false
        )
        form.setFieldValue('status', response.data.status)
        message.destroy()
        message.success('Товар включен')
        return
      }

      if (statusKey === EProductStatus.BLOCKED) {
        message.loading('Блокировка товара')
        await productApi.setActions({ toBlock: productId })
        form.setFieldValue('status', EProductStatus.BLOCKED)
        message.destroy()
        message.success('Товар заблокирован')
        return
      }

      const action =
        statusKey === EProductStatus.PUBLISHED
          ? EActionsProductRequest.PUBLISH
          : statusKey === EProductStatus.DRAFT
            ? EActionsProductRequest.DRAFT
            : EActionsProductRequest.REJECT
      const nextStatus = getStatusAfterAction(
        statusKey,
        form.getFieldValue('status'),
        role as ERoles
      )
      message.loading('Обновление статуса')
      await productApi.setActions({ [action]: productId })
      form.setFieldValue('status', nextStatus)
      message.destroy()
      message.success(
        nextStatus === EProductStatus.MODERATION
          ? 'Товар отправлен на модерацию'
          : 'Статус успешно сменен'
      )
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  React.useEffect(() => {
    getCategories()
  }, [])

  React.useEffect(() => {
    if (!isAdmin) {
      const getSeller = async () => {
        try {
          const response = await sellerApi.getSeller(sellerId as string)
          setSellerInfo(response.data)
        } catch (e) {
          showServerError(e)
        }
      }
      getSeller()
    }
  }, [])

  React.useEffect(() => {
    const defaultTax = sellerInfo?.default_tax
    if (
      isModeCreate &&
      typeof defaultTax === 'number' &&
      form.getFieldValue('tax') === undefined
    ) {
      form.setFieldValue('tax', defaultTax)
    }
  }, [sellerInfo])

  const onAddToDeletedVariations = React.useCallback(
    (productId: string) => {
      setDeletedVariations((prev) => [...prev, productId])
    },
    [deletedVariations]
  )

  React.useEffect(() => {
    if (mode === 'edit' && id) {
      // Сбрасываем состояние при изменении ID товара
      setIsLoading(true)
      setIsError(false)
      setParentAndVariationsData(null)
      setProductName('')
      setAcceptedAiGeneratedImage(undefined)
      setRatingScore(undefined)
      setSavedProduct(null)
      form.resetFields()

      const getProduct = async () => {
        try {
          message.loading('Загрузка данных о товаре')
          const { data } = await productApi.getProduct(id as string)

          let requiredSubCategoryCharacteristics: string[] = []
          let optionalSubCategoryCharacteristics: string[] = []
          if (data?.subCategory_id?._id) {
            try {
              const subCategoryResponse =
                await productApi.getSubCategoryCharacteristics(
                  data.subCategory_id._id
                )
              const subCategoryCharacteristics =
                subCategoryResponse.data.characteristics || []
              requiredSubCategoryCharacteristics =
                subCategoryCharacteristics
                  .filter((char) => char.required)
                  .map((char) => char.name)
              optionalSubCategoryCharacteristics =
                subCategoryCharacteristics
                  .filter((char) => !char.required)
                  .map((char) => char.name)
            } catch {}
          }

          // Названия в товаре и подкатегории расходятся по регистру и пробелам,
          // а в characteristic встречаются повторы — и то, и другое давало
          // одну характеристику дважды в карточке. Сводим к написанию подкатегории
          const canonicalName = (name?: string) => {
            const normalized = (name ?? '').trim().toLowerCase()
            return (
              [
                ...requiredSubCategoryCharacteristics,
                ...optionalSubCategoryCharacteristics
              ].find((item) => item.trim().toLowerCase() === normalized) ??
              (name ?? '').trim()
            )
          }

          const uniqueCharacteristics = (data?.characteristic ?? [])
            .map((char) => ({ ...char, name: canonicalName(char.name) }))
            .filter(
              (char, index, list) =>
                list.findIndex((item) => item.name === char.name) === index
            )

          const regularCharacteristics = uniqueCharacteristics.filter(
            (char) =>
              !requiredSubCategoryCharacteristics.includes(char.name) &&
              !optionalSubCategoryCharacteristics.includes(char.name)
          )

          const requiredCharacteristics = uniqueCharacteristics.filter((char) =>
            requiredSubCategoryCharacteristics.includes(char.name)
          )

          const optionalCharacteristics = uniqueCharacteristics.filter((char) =>
            optionalSubCategoryCharacteristics.includes(char.name)
          )
          form.setFields([
            { name: 'id', value: data._id },
            { name: 'number', value: data.number },
            { name: 'article', value: data.article },
            { name: 'external_item_id', value: data.external_item_id },
            { name: 'status', value: data.status },
            { name: 'type', value: data.type },
            { name: 'seller', value: data.seller_id },
            { name: 'name', value: data.name },
            {
              name: 'categories_id',
              value:
                data?.category_id?._id && data?.subCategory_id?._id
                  ? [data.category_id._id, data.subCategory_id._id]
                  : undefined
            },
            { name: 'vendor_code', value: data.vendor_code },
            { name: 'price', value: data.price },
            { name: 'tax', value: data.tax },
            { name: 'width', value: data?.dimension?.width },
            { name: 'length', value: data?.dimension?.length },
            { name: 'height', value: data?.dimension?.height },
            { name: 'weight', value: data?.dimension?.weight },
            { name: 'unit', value: data?.dimension?.unit },
            { name: 'in_stock', value: data?.dimension?.in_stock },
            {
              name: 'on_demand',
              // dimension в ответе типизирован alias-ом без on_demand — читаем через каст
              value: !!(data?.dimension as { on_demand?: boolean } | undefined)
                ?.on_demand
            },
            {
              name: 'auto_deduct_stock',
              // у товаров, созданных до появления поля, списание считается включённым
              value:
                (data?.dimension as { auto_deduct_stock?: boolean } | undefined)
                  ?.auto_deduct_stock !== false
            },
            {
              name: 'photo',
              value: data?.photo?.map((el) => ({
                uid: el,
                thumbUrl: `${IMAGES_URL}${el}`
              }))
            },
            { name: 'desc', value: data.desc },
            { name: 'characteristics', value: regularCharacteristics },
            { name: 'optionalCharacteristics', value: optionalCharacteristics },
            { name: 'requiredCharacteristics', value: requiredCharacteristics }
          ])
          if (data.variations?.length) {
            const [axis1Name, axis2Name] = deriveVariationAxes(
              data.characteristic,
              data.variations
            )
            const validVariations = data.variations.filter(
              (item) => item.characteristic?.length
            )
            const buildRow = (
              characteristics: ICharacteristic[] | undefined
            ): IFrontVariation =>
              ({
                firstCharacteristicName: axis1Name,
                firstCharacteristicValue: getCharacteristicValue(
                  characteristics,
                  axis1Name
                ),
                ...(axis2Name && {
                  secondCharacteristicName: axis2Name,
                  secondCharacteristicValue: getCharacteristicValue(
                    characteristics,
                    axis2Name
                  )
                })
              } as IFrontVariation)

            if (axis1Name) {
              form.setFieldValue('variations', [
                ...validVariations.map((item) => ({
                  ...buildRow(item.characteristic),
                  product: toVariationProduct(item)
                })),
                buildRow(undefined)
              ])
            }
          }
          setSelectedCategoryName(data?.category_id?.name ?? '')
          setSelectedSubCategoryName(data?.subCategory_id?.name ?? '')
          setProductName(data.name)
          setRatingScore(data.ratingScore)
          setSavedProduct({
            slug: data.slug,
            photo: data.photo,
            f_price: data.f_price
          })

          if (data.type === EProductType.VARIATION) {
            try {
              const parentResponse = await productApi.getParentAndVariations(
                data._id
              )
              setParentAndVariationsData(parentResponse.data)
            } catch {}
          }
        } catch (e) {
          const error = e as AxiosError
          if (
            error.response?.status === 400 ||
            error.response?.status === 404
          ) {
            return navigate('/')
          }
          showServerError(e)
          setIsError(true)
        } finally {
          setIsLoading(false)
          message.destroy()
        }
      }
      getProduct()
    }
  }, [mode, id])

  const closePreview = React.useCallback(() => {
    setPreview({ open: false, data: null })
    bodyScrollLock.disable()
  }, [])

  const closeCreateCategory = React.useCallback(() => {
    setIsCreateCategoryOpen(false)
    bodyScrollLock.disable()
  }, [])

  const deleteProduct = React.useCallback(
    (onSuccess?: () => void) => {
      bodyScrollLock.enable()
      Modal.confirm({
        centered: true,
        title: 'Вы действительно хотите удалить  товар?',
        content: 'Отменить данное действие будет невозможно.',
        icon: <ExclamationCircleOutlined />,
        okText: 'Удалить',
        cancelText: 'Закрыть',
        onOk() {
          if (onSuccess) {
            return onSuccess()
          }
          return new Promise((resolve) => {
            onDeleteProduct().then(resolve)
          })
        },
        onCancel() {
          bodyScrollLock.disable()
        }
      })
    },
    [productId]
  )

  const saveProduct = async (
    status:
      | EProductStatus.DRAFT
      | EProductStatus.PUBLISHED
      | EProductStatus.REJECTED
      | undefined
  ) => {
    try {
      bodyScrollLock.disable()
      closePreview()
      setIsSubmitting(true)
      // полный снимок стора: строки таблицы вариаций задаются без Form.Item,
      // а getFieldsValue() без аргумента отдаёт только зарегистрированные поля
      const data: IProductFormData = form.getFieldsValue(true)

      // Валидация вариаций - проверяем что все вариации имеют заполненные характеристики
      if (data.variations && data.variations.length > 0) {
        const incompleteVariations = data.variations.filter(
          (variation) => {
            // Пропускаем пустые строки-заготовки (без значений и без товара) в любой позиции:
            // заполненная строка может оказаться последней и не должна выпадать из проверки
            if (
              !variation.firstCharacteristicValue?.trim() &&
              !variation.secondCharacteristicValue?.trim() &&
              !variation.product
            ) {
              return false
            }

            // Проверяем что все непустые вариации имеют заполненные характеристики
            const hasFirstChar =
              variation.firstCharacteristicName &&
              variation.firstCharacteristicValue?.trim()
            const hasSecondChar =
              !variation.secondCharacteristicName ||
              variation.secondCharacteristicValue?.trim()

            return !hasFirstChar || !hasSecondChar
          }
        )

        if (incompleteVariations.length > 0) {
          message.error('Заполните все характеристики для вариаций товара')
          setIsSubmitting(false)
          return
        }
      }
      const normalizedData = isModeCreate
        ? getCreateSubmitData(
          data,
          status,
          firstVariationName,
          secondVariationName
        )
        : getEditSubmitData(
          data,
          status,
          productId ?? '',
          deletedPhotos,
          deletedVariations,
          firstVariationName,
          secondVariationName,
          acceptedAiGeneratedImage
        )
      const response = isModeCreate
        ? await productApi.addProduct(normalizedData as ICreateProduct)
        : await productApi.updateProduct(normalizedData as IUpdateProduct)
      const savedStatus: EProductStatus | undefined = response.data?.status
      if (!isModeCreate && savedStatus) {
        form.setFieldValue('status', savedStatus)
      }
      if (!isModeCreate && response.data) {
        setSavedProduct({
          slug: response.data.slug,
          photo: response.data.photo,
          f_price: response.data.f_price
        })
      }
      setAcceptedAiGeneratedImage(undefined)
      if (status === EProductStatus.DRAFT) {
        return message.info('Товар сохранен как черновик')
      }
      // Отклонённый товар и товар из подкатегории на модерации бэкенд отправляет
      // на модерацию, а не публикует
      if (
        status === EProductStatus.PUBLISHED &&
        savedStatus === EProductStatus.MODERATION
      ) {
        return message.info('Товар отправлен на модерацию')
      }
      if (status === EProductStatus.PUBLISHED) {
        return message.success('Товар успешно опубликован')
      }
      message.success('Товар успешно сохранен')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFinish = async (
    status:
      | EProductStatus.DRAFT
      | EProductStatus.PUBLISHED
      | EProductStatus.REJECTED
      | undefined
  ) => {
    if (
      status &&
      productStatus === EProductStatus.MODERATION &&
      role === ERoles.USER
    ) {
      if (status === EProductStatus.PUBLISHED) {
        return showMessage(
          'error',
          'Этот товар находится на модерации, вы не можете его опубликовать',
          ''
        )
      }
      return showMessage(
        'error',
        'Этот товар находится на модерации, вы не можете сохранить его в черновик',
        ''
      )
    }
    if (!(await validateProductForm(form, status ?? productStatus))) {
      return showMessage('error', 'Заполните все поля корректно', '')
    }
    // полный снимок стора: строки таблицы вариаций задаются без Form.Item,
    // а validateFields() и getFieldsValue() без аргумента отдают только зарегистрированные поля
    const data: IProductFormData = form.getFieldsValue(true)
    if (data.variations) {
      // характеристики основного товара — для запрета вариаций-дублей его комбинации
      const parentCharacteristics = [
        ...(data.requiredCharacteristics || []),
        ...(data.optionalCharacteristics || []),
        ...(data.characteristics || [])
      ]
      const { isError } = validateVariations(data.variations, parentCharacteristics)
      if (isError) return
    }
    if (status === EProductStatus.PUBLISHED) {
      bodyScrollLock.enable()
      return setPreview({
        open: true,
        data: getPreviewData(
          data,
          selectedCategoryName,
          selectedSubCategoryName
        )
      })
    }
    saveProduct(status)
  }

  const handleAiModalClose = () => {
    setIsAiEnhancementModalOpen(false)
  }

  const handleAiModalAfterClose = () => {
    setAiButtonCoordinates(null)
  }

  const getAiImageThumbUrl = (image: string) => {
    if (/^https?:\/\//i.test(image)) {
      return image
    }

    return `${IMAGES_URL}${image}`
  }

  const getAiImageFile = async (image: string) => {
    const imageUrl = getAiImageThumbUrl(image)
    const response = await fetch(imageUrl)

    if (!response.ok) {
      throw new Error('Не удалось скачать AI-картинку')
    }

    const blob = await response.blob()
    const urlFileName = imageUrl.split('?')[0].split('/').pop()
    const fileName = urlFileName || 'ai-product-photo.webp'

    return new File([blob], fileName, {
      type: blob.type || 'image/webp'
    })
  }

  const handleApplyAiEnhancement = async (data: IAiEnhancementApplyData) => {
    if (data.description !== undefined) {
      form.setFieldValue('desc', data.description)
    }

    if (data.image) {
      try {
        if (!data.replacedImageUid) {
          throw new Error('Не выбрано фото для замены')
        }

        if (!productId && !id) {
          throw new Error('Не найден ID товара')
        }

        const file = await getAiImageFile(data.image)
        const { data: updatedProduct } = await productApi.replaceProductPhoto(
          productId ?? id ?? '',
          data.replacedImageUid,
          file
        )

        form.setFieldValue(
          'photo',
          updatedProduct.photo?.map((photo) => ({
            uid: photo,
            thumbUrl: `${IMAGES_URL}${photo}`
          })) ?? []
        )
        setDeletedPhotos((prev) =>
          prev.filter((photo) => photo !== data.replacedImageUid)
        )
        setAcceptedAiGeneratedImage(undefined)
      } catch (e) {
        showServerError(e)
        throw e
      }
    }

    showMessage('success', 'AI-изменения применены к форме', '')
  }

  const handleGoToParent = () => {
    if (parentAndVariationsData?.parent?._id) {
      navigate(`/goods/edit/${parentAndVariationsData.parent._id}`)
    }
  }

  const handleDetachProduct = async () => {
    if (!productId) return

    try {
      setIsDetaching(true)
      await productApi.makeProductIndependent(productId)
      message.success('Товар успешно отсоединен от основного товара')
      // Обновляем тип товара в форме
      form.setFieldValue('type', EProductType.PRODUCT)
      // Очищаем данные о родительском товаре
      setParentAndVariationsData(null)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsDetaching(false)
    }
  }

  const siteUnavailableReason = getSiteUnavailableReason({
    slug: savedProduct?.slug,
    photo: savedProduct?.photo,
    status: productStatus,
    type: productType,
    parentStatus: parentAndVariationsData?.parent?.status,
    seller
  })

  const openOnSite = () => {
    window.open(
      `${STOREFRONT_URL}product/${savedProduct?.slug}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  // У вариации своих строк вариаций нет: сайт собирает «Параметры»
  // из основного товара и всех его вариаций
  const getParentVariationRows = (): IFrontVariation[] => {
    if (!parentAndVariationsData?.parent) return []
    const { parent, variations: siblings } = parentAndVariationsData
    const [axis1Name, axis2Name] = deriveVariationAxes(
      parent.characteristic,
      siblings
    )
    if (!axis1Name) return []
    return [parent, ...siblings].map(
      (item) =>
        ({
          firstCharacteristicName: axis1Name,
          firstCharacteristicValue: getCharacteristicValue(
            item.characteristic,
            axis1Name
          ),
          ...(axis2Name && {
            secondCharacteristicName: axis2Name,
            secondCharacteristicValue: getCharacteristicValue(
              item.characteristic,
              axis2Name
            )
          })
        } as IFrontVariation)
    )
  }

  const openCardPreview = () => {
    const data = getPreviewData(
      form.getFieldsValue(true),
      selectedCategoryName,
      selectedSubCategoryName
    )
    setCardPreview(
      productType === EProductType.VARIATION
        ? { ...data, variations: getParentVariationRows() }
        : data
    )
  }

  const openAiEnhancement = () => {
    if (aiButtonRef.current) {
      setAiButtonCoordinates(aiButtonRef.current.getBoundingClientRect())
    }
    setIsAiEnhancementModalOpen(true)
  }

  const actions =
    isLoading || isError ? null : (
      <>
        {!isModeCreate && (
          <Tooltip title={siteUnavailableReason}>
            {/* Неактивная кнопка не получает событий мыши — подсказку ловит обёртка */}
            <span
              className={classNames(styles.openOnSite, {
                [styles.openOnSiteDisabled]: !!siteUnavailableReason
              })}
            >
              <Button
                htmlType='button'
                size='large'
                block
                icon={<ExportOutlined />}
                onClick={openOnSite}
                disabled={!!siteUnavailableReason}
              >
                Открыть
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          htmlType='button'
          size='large'
          block
          icon={<EyeOutlined />}
          onClick={openCardPreview}
        >
          Просмотр
        </Button>
        {role === ERoles.USER && (
          <>
            <Button
              htmlType='button'
              type='primary'
              size='large'
              block
              icon={<AppstoreAddOutlined />}
              onClick={() => onFinish(EProductStatus.PUBLISHED)}
              loading={isSubmitting}
            >
              {productStatus === EProductStatus.REJECTED
                ? 'Отправить на модерацию'
                : 'Опубликовать'}
            </Button>
            {productStatus !== EProductStatus.REJECTED && (
              <Button
                htmlType='button'
                size='large'
                block
                icon={<SaveOutlined />}
                onClick={() => onFinish(EProductStatus.DRAFT)}
                loading={isSubmitting}
              >
                Сохранить как черновик
              </Button>
            )}
          </>
        )}
        {!isModeCreate && (
          <>
            <Button
              htmlType='button'
              type={role === ERoles.ADMIN ? 'primary' : 'default'}
              size='large'
              block
              icon={<SaveOutlined />}
              onClick={() => onFinish(undefined)}
              loading={isSubmitting}
            >
              Сохранить
            </Button>
            <OrangeButton
              size='large'
              block
              onClick={openAiEnhancement}
              ref={aiButtonRef}
            >
              AI-улучшение
            </OrangeButton>
            <Button
              htmlType='button'
              size='large'
              block
              icon={<DeleteOutlined />}
              onClick={() => deleteProduct()}
              className={styles.deleteButton}
            >
              Удалить
            </Button>
          </>
        )}
      </>
    )

  return (
    <>
      <AnchorsWrapper
        title={
          isModeCreate
            ? 'Добавить товар'
            : isAdmin
              ? `Редактировать товар ${article ?? ''}`
              : 'Редактировать товар'
        }
        anchors={anchors}
        titleId='info'
        actions={actions}
      >
        <div>
          {isLoading || isError ? null : (
            <Form form={form} name='product' size='large'>
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Typography.Title
                    level={2}
                    className={styles.sectionTitle}
                    style={{ marginBottom: 0 }}
                  >
                    Информация о товаре
                  </Typography.Title>
                  <ProductStatus
                    activeStatus={productStatus}
                    onStatusClick={changeStatus}
                    className={styles.productStatus}
                  />
                </div>

                {productType && (
                  <ProductInfoBanner
                    productType={productType}
                    onGoToParent={handleGoToParent}
                    onDetachProduct={handleDetachProduct}
                    isDetaching={isDetaching}
                  />
                )}
                <ProductRating
                  mode={mode}
                  ratingScore={ratingScore}
                  productType={productType}
                />
                {isAdmin && (
                  <LargeText className={styles.seller}>
                    Продавец:{' '}
                    <Link to={getSellerEditPath(seller?._id ?? '')}>
                      <span style={{ color: 'rgba(36, 127, 255, 1)' }}>
                        {seller?.shop_details?.shop_name}
                      </span>
                    </Link>
                  </LargeText>
                )}
                <Form.Item
                  name='name'
                  label='Название товара'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните название товара'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните название товара'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ваше название' />
                </Form.Item>
                <div className={styles.flexSmall}>
                  <Form.Item
                    name='categories_id'
                    label='Категория'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите категорию'
                      }
                    ]}
                    className={styles.inputFlex}
                    hasFeedback
                    // Категория вариации наследуется от основного товара — менять нельзя
                    tooltip={
                      productType === EProductType.VARIATION
                        ? 'Категория вариации наследуется от основного товара'
                        : undefined
                    }
                  >
                    <Cascader
                      showSearch
                      placeholder='Выбор категории'
                      rootClassName='myCascader'
                      popupClassName={styles.categorySelect}
                      options={categories}
                      fieldNames={{ label: 'title' }}
                      expandTrigger='click'
                      disabled={productType === EProductType.VARIATION}
                      onChange={(value, options) => {
                        form.setFieldValue('categories_id', value)
                        setSelectedCategoryName(options?.[0]?.title ?? '')
                        setSelectedSubCategoryName(options?.[1]?.title ?? '')
                      }}
                    />
                  </Form.Item>
                  {isAdmin && (
                    <Button
                      htmlType='button'
                      icon={<PlusOutlined />}
                      onClick={() => {
                        bodyScrollLock.enable()
                        setIsCreateCategoryOpen(true)
                      }}
                    >
                      Добавить категорию
                    </Button>
                  )}
                </div>
                {deliveryRestriction && (
                  <DeliveryRestrictionNote restriction={deliveryRestriction} />
                )}
                {!isModeCreate && (
                  <Form.Item name='article' label='Артикул площадки'>
                    <Input disabled />
                  </Form.Item>
                )}
                <Form.Item
                  name='vendor_code'
                  label='Артикул продавца'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните артикул продавца'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните артикул продавца'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ваш артикул продавца' />
                </Form.Item>
                <div
                  className={styles.flexBig}
                  style={{ marginBottom: isAdmin ? 24 : 15 }}
                >
                  <Form.Item
                    name='price'
                    label='Стоимость'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните стоимость товара'
                      },
                      {
                        pattern: floatNumbersPattern,
                        message: 'Заполните стоимость товара корректно'
                      },
                      {
                        pattern: nonZeroPattern,
                        message: 'Стоимость должна быть больше нуля'
                      }
                    ]}
                    className={styles.inputFlex}
                    hasFeedback
                    extra={
                      !isModeCreate && (
                        <Button
                          type='link'
                          size='small'
                          className={styles.priceHistoryLink}
                          onClick={() => setIsPriceHistoryOpen(true)}
                        >
                          История изменений стоимости
                        </Button>
                      )
                    }
                  >
                    <Input placeholder='Цена в рублях для покупателя' />
                  </Form.Item>
                  <Form.Item
                    name='tax'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите размер налога'
                      }
                    ]}
                    style={{ width: 238, marginBottom: 0 }}
                    hasFeedback
                  >
                    <Select
                      placeholder='Размер налога'
                      options={TAX_OPTIONS}
                    />
                  </Form.Item>
                </div>
                {!isAdmin && (
                  <InfoBlock
                    text={
                      <>
                        <span>
                          При указании стоимости учитывайте комиссию сервиса:
                        </span>{' '}
                        <Typography.Text
                          className={styles.commissionInfoPercent}
                        >
                          {sellerInfo?.commission}%
                        </Typography.Text>
                      </>
                    }
                  />
                )}
              </section>
              <section id='dimensions' className={styles.section}>
                <Typography.Title
                  level={2}
                  className={classNames(
                    styles.sectionTitle,
                    styles.dimensionsTitle
                  )}
                >
                  Габариты (см)
                </Typography.Title>
                <Form.Item
                  name='width'
                  label='Ширина'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните ширину упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните ширину упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Ширина должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ширина упакованного товара в сантиметрах' />
                </Form.Item>
                <Form.Item
                  name='length'
                  label='Длина'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните длину упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните длину упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Длина должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Длина упакованного товара в сантиметрах' />
                </Form.Item>
                <Form.Item
                  name='height'
                  label='Высота'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните высоту упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните высоту упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Высота должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Высота упакованного товара в сантиметрах' />
                </Form.Item>
                <div className={styles.weightRow}>
                  <Form.Item
                    name='weight'
                    label='Вес'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните вес товара'
                      },
                      {
                        pattern: floatNumbersPattern,
                        message: 'Заполните вес товара корректно'
                      },
                      {
                        pattern: nonZeroPattern,
                        message: 'Вес должен быть больше нуля'
                      }
                    ]}
                    className={styles.inputFlex}
                    hasFeedback
                  >
                    <Input placeholder='Общий вес товара' />
                  </Form.Item>
                  <Form.Item
                    name='unit'
                    label='Единицы измерения'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите единицы измерения'
                      }
                    ]}
                    className={styles.unitField}
                    hasFeedback
                  >
                    <Select
                      placeholder='Единицы измерения'
                      options={[
                        { value: EUnit.GR, label: 'Граммы' },
                        { value: EUnit.KG, label: 'Килограммы' }
                      ]}
                    />
                  </Form.Item>
                </div>
                <StockFields form={form} />
              </section>
              <section id='filling' className={styles.section}>
                <Typography.Title
                  level={2}
                  className={styles.sectionTitle}
                  style={{ marginBottom: 17 }}
                >
                  Наполнение
                </Typography.Title>
                <div className={styles.subSection}>
                  <Typography.Title
                    level={4}
                    className={styles.sectionSubTitle}
                    style={{ marginBottom: 3 }}
                  >
                    Фото товара:
                  </Typography.Title>
                  <Uploader
                    multiple
                    sortable
                    minSize={MIN_PRODUCT_IMAGE_SIDE}
                    hint={
                      <>
                        Минимальный формат: 600x600 px
                        <br />
                        Рекомендуемый формат 900x1200
                        <br />
                        Поддерживаются форматы JPG, JPEG, PNG, WebP
                        <br />
                        Первое фото — главное. Порядок меняется перетаскиванием
                      </>
                    }
                    maxCount={100}
                    name='photo'
                    rules={[
                      {
                        required: true,
                        message: 'Добавьте хотя бы 1 фотографию товара'
                      }
                    ]}
                    onRemoveClick={(file) => {
                      if (file.uid === acceptedAiGeneratedImage) {
                        setAcceptedAiGeneratedImage(undefined)
                        return
                      }
                      if (!file.originFileObj) {
                        setDeletedPhotos((prev) => [...prev, file.uid])
                      }
                    }}
                  />
                </div>
                <div className={styles.subSection}>
                  <div className={styles.descriptionHeader}>
                    <Typography.Title
                      level={4}
                      className={styles.sectionSubTitle}
                      style={{ marginBottom: 0 }}
                    >
                    Описание:
                    </Typography.Title>
                  </div>
                  <Form.Item
                    name='desc'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните описание товара'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните описание товара'
                      }
                    ]}
                    hasFeedback
                    valuePropName='modelValue'
                  >
                    <MdEditor
                      language='ru'
                      noUploadImg
                      style={{ height: '400px' }}
                    />
                  </Form.Item>
                </div>
                <Characteristics
                  form={form}
                  sellerId={seller?._id ?? (sellerId as string)}
                  productId={productId}
                  productType={productType}
                  firstVariationName={firstVariationName}
                  secondVariationName={secondVariationName}
                  addToDeletedVariations={onAddToDeletedVariations}
                />
              </section>
            </Form>
          )}
        </div>
        <ProductPreview
          mode={mode}
          form={form}
          open={preview.open}
          data={preview.data}
          firstVariationName={firstVariationName}
          secondVariationName={secondVariationName}
          close={closePreview}
          submit={saveProduct}
          deleteProduct={deleteProduct}
        />
        <ProductCardPreview
          open={!!cardPreview}
          data={cardPreview}
          article={article}
          sellerName={
            seller?.shop_details?.shop_name ??
            sellerInfo?.shop_details?.shop_name
          }
          fullPrice={savedProduct?.f_price}
          onDemand={!!form.getFieldValue('on_demand')}
          isDisabled={productStatus === EProductStatus.DISABLED}
          close={() => setCardPreview(null)}
        />
        <PriceHistoryModal
          productId={isPriceHistoryOpen && id ? id : null}
          onClose={() => setIsPriceHistoryOpen(false)}
        />
        {isAdmin && (
          <CreateCategory
            isOpen={isCreateCategoryOpen}
            close={closeCreateCategory}
            categories={categories}
            onSuccess={getCategories}
          />
        )}
      </AnchorsWrapper>
      <AiEnhancementModal
        type='create'
        productId={id as string}
        productName={productName}
        visible={isAiEnhancementModalOpen}
        onClose={handleAiModalClose}
        buttonCoordinates={aiButtonCoordinates}
        afterModalClose={handleAiModalAfterClose}
        currentDescription={currentDescription}
        photos={currentPhotos}
        onApply={handleApplyAiEnhancement}
      />
    </>
  )
}
